// R17 Wave 0 extraction, R17 Task A firm-totals reconciliation columns
import React from 'react';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import type { DateRange, UserReportSummary } from '../../types/report';

interface UserReportFirmTotalsCardProps {
  summary: UserReportSummary;
  dateRange: DateRange;
}

/**
 * Firm-totals / top summary card for the User Report page. Extracted from
 * UserReport.tsx in R17 Wave 0 (pre-Task A) so Task A can hang the
 * reconciliation columns off this component without further inflating
 * the parent file.
 *
 * R17 Task A: renders the four reconciliation tiles (Trade Gross / Check
 * Gross / Trade User Comm / Check User Comm) from summary.firm_totals
 * alongside the existing user_count / transaction_count / total tiles.
 * Blue = trade side, emerald = check side (matches the per-user card).
 * Falls back gracefully when firm_totals is absent (pre-R17 cached data).
 */
const UserReportFirmTotalsCard: React.FC<UserReportFirmTotalsCardProps> = ({ summary, dateRange }) => {
  const ft = summary.firm_totals;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Summary</h2>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Period</p>
          <p className="text-sm font-semibold text-gray-900">
            {formatDate(dateRange.start_date)} to {formatDate(dateRange.end_date)}
          </p>
        </div>
        <div className="bg-emerald-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Total Gross Commission</p>
          <p className="text-lg font-semibold text-emerald-700">
            {formatCurrency(summary.total_gross_commission ?? 0)}
          </p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Total Commission</p>
          <p className="text-lg font-semibold text-blue-700">
            {formatCurrency(summary.total_commission)}
          </p>
        </div>
        <div className="bg-primary-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Total Transactions</p>
          <p className="text-lg font-semibold text-primary-700">
            {summary.transaction_count}
          </p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Employees</p>
          <p className="text-lg font-semibold text-green-700">
            {summary.user_count}
          </p>
        </div>
      </div>

      {/* R17 Task A: firm-wide reconciliation block (Peter #7/#8/#9) —
          four split tiles + count-parity tiles so Peter can cross-check
          each side against the trading system. Only renders when the
          backend has attached firm_totals. */}
      {ft && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Firm-Wide Reconciliation</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Trade Gross</p>
              <p className="text-base font-semibold text-blue-700">
                {formatCurrency(ft.trade_gross_commission)}
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Check Gross</p>
              <p className="text-base font-semibold text-emerald-700">
                {formatCurrency(ft.check_gross_commission)}
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Trade User Comm</p>
              <p className="text-base font-semibold text-blue-700">
                {formatCurrency(ft.trade_user_commission)}
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Check User Comm</p>
              <p className="text-base font-semibold text-emerald-700">
                {formatCurrency(ft.check_user_commission)}
              </p>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-6 text-xs text-gray-600">
            <div>
              Trade Transactions:{' '}
              <span className="font-semibold text-gray-900">{ft.trade_transaction_count}</span>
            </div>
            <div>
              Checks:{' '}
              <span className="font-semibold text-gray-900">{ft.check_count}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserReportFirmTotalsCard;
